import type { PluginWebManifest } from '@reflex/plugin-api/web';

const PKG = '@reflex/plugin-tutorial';

/**
 * Web manifest for the tutorial plugin.
 *
 * - The launcher route at `/tutorial/:tutorialName` is `rootLevel: true` so
 *   deep links work without an org slug. It still mounts inside `AuthGate`
 *   and `AppShell`; the launcher itself navigates to the first step's path
 *   (which is org-scoped) once the tutorial starts.
 * - The overlay is a `globalUiElement` so it persists across navigation
 *   inside `AppShell`. The component returns `null` when no tutorial is
 *   active so the host can mount it unconditionally.
 */
export const tutorialWeb: PluginWebManifest = {
  routes: [
    {
      path: '/tutorial/:tutorialName',
      componentPath: `${PKG}/web/TutorialLauncher`,
      rootLevel: true,
    },
    {
      path: '/tutorials',
      componentPath: `${PKG}/web/TutorialsPage`,
      rootLevel: true,
    },
  ],
  navItems: [
    {
      to: '/tutorials',
      label: 'Tutorials',
      icon: 'GraduationCap',
      group: 'user-menu',
      rootLevel: true,
      order: 20,
    },
  ],
  // The account dropdown carries only actions and live-state rows, so the
  // library is reached from the Account takeover's nav or from here.
  commands: [
    {
      to: '/tutorials',
      label: 'Tutorials',
      icon: 'GraduationCap',
      group: 'settings',
      rootLevel: true,
    },
  ],
  globalUiElements: [
    {
      id: 'tutorial-overlay',
      componentPath: `${PKG}/web/TutorialOverlay`,
    },
    {
      id: 'tutorial-completion-tracker',
      componentPath: `${PKG}/web/TutorialCompletionTracker`,
    },
    {
      // Renders the universal "before you begin" intro card whenever a tour
      // has been offered (page-banner Start, /tutorials/:name route, or the
      // first-visit auto-prompt) but not yet confirmed.
      id: 'tutorial-start-confirm',
      componentPath: `${PKG}/web/TutorialStartConfirm`,
    },
    {
      // Headless: detects the first-visit condition for the `intro` tour and
      // asks the store to offer it. The card itself is drawn by
      // `tutorial-start-confirm`.
      id: 'tutorial-first-visit-prompt',
      componentPath: `${PKG}/web/TutorialFirstVisitPrompt`,
    },
    {
      // Per-page banner listing not-yet-completed guided tours. Portals into
      // whichever `[data-page-banner-region]` slot the active page layout
      // exposes — `PageHeader` (below title/description) for single-pane
      // pages, `TwoPanelLayout` (above the sidebar) for two-pane pages.
      // Returns null when the current page has nothing to offer.
      id: 'tutorial-page-banner',
      componentPath: `${PKG}/web/TutorialPageBanner`,
    },
    {
      // Test-only: attaches `window.__reflexTutorial` for the Playwright tour
      // harness, gated on `window.__REFLEX_E2E__`. Inert in normal sessions.
      id: 'tutorial-e2e-bridge',
      componentPath: `${PKG}/web/TutorialE2EBridge`,
    },
  ],
};
